import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { portalAPI } from '../lib/api';
import { Lock, Check, AlertCircle } from 'lucide-react';

export default function PortalActivatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError('Lien d\'activation invalide.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Le mot de passe doit faire au moins 8 caractères'); return; }
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas'); return; }
    setLoading(true);
    try {
      await portalAPI.setPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/portail/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'activation');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-primary)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/branding/logo-white.png" alt="Adalia" className="h-16 mx-auto mb-4" />
          <h1 className="text-xl font-serif" style={{ color: 'var(--text-primary)' }}>Activation de votre compte</h1>
          <p className="text-[12px] mt-1" style={{ color: 'var(--text-tertiary)' }}>Définissez votre mot de passe pour accéder au portail client</p>
        </div>

        {success ? (
          <div className="rounded-[12px] p-6 text-center" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3" style={{ background: 'rgba(34,197,94,0.15)' }}>
              <Check className="w-6 h-6" style={{ color: '#22C55E' }} />
            </div>
            <div className="text-[14px] font-medium mb-1" style={{ color: 'var(--text-primary)' }}>Compte activé !</div>
            <div className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Redirection vers la page de connexion...</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-[12px] p-6 space-y-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} data-testid="portal-activate-form">
            {error && <div className="flex items-start gap-2 px-3 py-2 rounded text-[12px] text-red-400" style={{ background: 'rgba(220,38,38,0.1)' }}><AlertCircle className="w-4 h-4 mt-0.5" />{error}</div>}
            <div>
              <label className="block text-[11px] uppercase tracking-[1px] mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Nouveau mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} placeholder="Minimum 8 caractères" className="w-full pl-10 pr-3 py-2.5 rounded-[8px] text-[13px] outline-none" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="activate-password" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[1px] mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Confirmer le mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="w-full pl-10 pr-3 py-2.5 rounded-[8px] text-[13px] outline-none" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} data-testid="activate-confirm" />
              </div>
            </div>
            <button type="submit" disabled={loading || !token} className="w-full px-4 py-2.5 rounded-[8px] text-[13px] font-semibold disabled:opacity-50" style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }} data-testid="activate-submit">
              {loading ? 'Activation...' : 'Activer mon compte'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
